/**
 * Counts a figure up once, the first time it comes into view.
 *
 * The point is to make proof feel alive, not to be clever: it runs once per element, never
 * loops, and never runs at all under prefers-reduced-motion — where the final value is
 * simply there from the start.
 *
 * The final value is in the markup before this script does anything, so a reader with no
 * JavaScript, or a crawler, sees the real number rather than a zero.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Ease-out, so the number decelerates into place rather than stopping dead. */
const easeOut = (t) => 1 - Math.pow(1 - t, 3);

class BeeulCountUp extends HTMLElement {
  connectedCallback() {
    const target = Number(this.getAttribute('value'));
    if (!Number.isFinite(target) || REDUCED_MOTION.matches) return;

    this.decimals = Number(this.getAttribute('decimals')) || 0;
    this.duration = Number(this.getAttribute('duration')) || 1400;
    this.target = target;
    this.output = this.querySelector('[data-beeul-count-value]');
    if (!this.output) return;

    this.final = this.output.textContent;

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.observer.disconnect();
          this.run();
        }
      },
      { threshold: 0.5 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  run() {
    const start = performance.now();

    const step = (now) => {
      const progress = Math.min((now - start) / this.duration, 1);
      const value = this.target * easeOut(progress);

      if (progress < 1) {
        this.output.textContent = value.toLocaleString('en-MY', {
          minimumFractionDigits: this.decimals,
          maximumFractionDigits: this.decimals,
        });
        requestAnimationFrame(step);
      } else {
        // Restore the authored string so any suffix or formatting survives exactly.
        this.output.textContent = this.final;
      }
    };

    requestAnimationFrame(step);
  }
}

if (!customElements.get('beeul-count-up')) {
  customElements.define('beeul-count-up', BeeulCountUp);
}
